import { put, call } from 'redux-saga/effects';
import { InvoicesService } from '../../../services/Invoices';
import {
  EDocumentType,
  IInvoiceBilletResponse,
  IInvoicesResponse
} from '../invoices/types';
import { LongSteelOrdersService } from '../../../services/LongSteelOrders';
import {
  EOrderTab,
  IBilledOrderData,
  ILongSteelOrdersResponse,
  IQCFileResponse,
  IDownloadNFEResponse
} from '../long-steel-orders/types';
import { ECustomerTypeView } from '../user/types';
import {
  loadCustomerNfsAndQualityCertificatesSucces,
  loadCustomerNfsAndQualityCertificatesFailure,
  loadCustomerBilletsFailure,
  loadCustomerBilletsSuccess,
  loadQCFileSuccess,
  loadQCFileFailure,
  loadBilletFileSuccess,
  loadBilletFileFailure,
  loadNFFileSuccess,
  loadNFFileFailure,
  loadNFXMLFileSuccess,
  loadNFXMLFileFailure
} from './action';
import {
  ICustomerBilletItem,
  ICustomerQualityCertificateItem,
  ICustomerNFItem,
  Types
} from './types';
import { AxiosError } from 'axios';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { DownloadAreaService } from '../../../services/DownloadArea';

interface ILoadCustomerNFsParams {
  payload: {
    customerId: string;
    startDate: string;
    endDate: string;
    customerViewType: ECustomerTypeView;
  };
  type: Types;
}

interface ILoadCustomerBilletsParams {
  payload: {
    customerId: string;
    customerViewType: ECustomerTypeView;
    startDate: string;
    endDate: string;
  };
  type: Types;
}

interface ICustomerQualityCertificateParams {
  payload: {
    qualityCertificateItem: ICustomerQualityCertificateItem;
  };
  type: Types;
}
interface ILoadBilletFileParams {
  payload: {
    billetItem: ICustomerBilletItem;
  };
  type: Types;
}

interface ILoadNFFileParams {
  payload: {
    nfItem: ICustomerNFItem;
  };
  type: Types;
}

interface ILoadNFXMLFileParams {
  payload: {
    nfXmlItem: ICustomerNFItem;
  };
  type: Types;
}

export function* loadCustomerNfsAndQualityCertificatesSaga(
  params: ILoadCustomerNFsParams
) {
  const { customerId, startDate, endDate, customerViewType } = params.payload;
  try {
    const longSteelOrderSvc = new LongSteelOrdersService();
    const ordersResponse: ILongSteelOrdersResponse =
      yield longSteelOrderSvc.getOrders(
        [],
        [customerId],
        customerViewType,
        EOrderTab.BILLED,
        startDate,
        endDate
      );
    const billedOrders = ordersResponse.items as IBilledOrderData[];

    yield put(loadCustomerNfsAndQualityCertificatesSucces(billedOrders));
  } catch (err) {
    console.error('loadCustomerNfsAndQualityCertificatesSaga', err);
    yield put(loadCustomerNfsAndQualityCertificatesFailure(err as AxiosError));
  }
}

export function* loadCustomerBilletsSaga(params: ILoadCustomerBilletsParams) {
  const { customerId, customerViewType, startDate, endDate } = params.payload;
  try {
    const invoicesService = new InvoicesService();

    const invoicesResponse: IInvoicesResponse =
      yield invoicesService.getDuplicates(
        customerId,
        customerViewType,
        startDate,
        endDate,
        'A'
      );

    const billets: ICustomerBilletItem[] = invoicesResponse.invoices
      ?.filter((invoice) => invoice.documentType !== EDocumentType.NOTA_JUROS)
      .map((invoice, idx) => ({
        key: idx,
        orderNumber: invoice.purchaseOrder,
        totalValue: invoice.openInvoiceAmount,
        billingDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        status: invoice.status,
        taxNumberAccount: invoice.taxNumberAccount,
        customerCnpj: invoice.customerCnpj,
        bankSlipNumber: invoice.bankSlipNumber,
        bankCode: invoice.bankCode,
        invoiceNumber: invoice.invoiceNumber,
        documentType: invoice.documentType,
        invoiceType: invoice.invoiceType
      })) as ICustomerBilletItem[];

    yield put(loadCustomerBilletsSuccess(billets));
  } catch (err) {
    console.error('loadCustomerBilletsSaga', err);
    yield put(loadCustomerBilletsFailure(err as AxiosError));
  }
}

export function* loadDownloadQCFileSaga(
  params: ICustomerQualityCertificateParams
) {
  const { qualityCertificateItem } = params.payload;
  try {
    const longSteelOrdersService = new LongSteelOrdersService();
    const { cqNumber, deliveryCode, deliveryItemCode } = qualityCertificateItem;

    const qcFileResponse: IQCFileResponse =
      yield longSteelOrdersService.loadQCFile(
        cqNumber,
        deliveryCode,
        deliveryItemCode
      );

    yield put(loadQCFileSuccess(qualityCertificateItem, qcFileResponse));
  } catch (err) {
    console.error('loadQCFile', err);
    yield put(
      addToast({
        message: `Erro inesperado, tente novamente mais tarde. Se o erro persistir, procure o seu vendedor.`,
        type: EToastTypes.DANGER,
        analyticsTag: {
          screenName: 'LG:BuscarDocumentos:CertificadoDeQualidade',
          itemName: 'error:erro_inesperado',
          event: 'error',
          itemDescription:
            'buscarDocumentos_certificadoDeQualidade_erroInesperado',
          nonInteraction: true
        }
      })
    );
    yield put(loadQCFileFailure(qualityCertificateItem, err as AxiosError));
  }
}
export function* loadBilletFileSaga(params: ILoadBilletFileParams) {
  const { billetItem } = params.payload;
  try {
    const invoicesService = new InvoicesService();
    const {
      taxNumberAccount,
      dueDate,
      customerCnpj,
      bankSlipNumber,
      bankCode,
      invoiceType
    } = billetItem;

    const invoiceBilletResponse: IInvoiceBilletResponse =
      yield invoicesService.getBilletInvoice(
        taxNumberAccount,
        dueDate,
        customerCnpj,
        bankSlipNumber,
        bankCode,
        invoiceType
      );

    yield put(loadBilletFileSuccess(billetItem, invoiceBilletResponse));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(
      addToast({
        message: `Erro inesperado, tente novamente mais tarde. Se o erro persistir, procure o seu vendedor.`,
        type: EToastTypes.DANGER
      })
    );
    yield put(loadBilletFileFailure(billetItem, err as AxiosError));
  }
}

export function* loadNFFileSaga(params: ILoadNFFileParams) {
  const { nfItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const downloadNFEResponse: IDownloadNFEResponse = yield call(
      ordersService.loadNFEFile.bind(ordersService),
      nfItem.danfeNumber
    );
    yield put(loadNFFileSuccess(nfItem, downloadNFEResponse));
  } catch (err) {
    yield put(
      addToast({
        message:
          'Erro inesperado, tente novamente mais tarde. Se o erro persistir, procure o seu vendedor.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadNFFileFailure(nfItem, err));
  }
}

export function* loadNFXMLFileSaga(params: ILoadNFXMLFileParams) {
  const { nfXmlItem } = params.payload;
  try {
    const ordersService = new DownloadAreaService();

    const downloadNFEResponse: IDownloadNFEResponse = yield call(
      ordersService.loadNFXMLDownloadFile.bind(ordersService),
      nfXmlItem.billingNumber
    );
    yield put(loadNFXMLFileSuccess(nfXmlItem, downloadNFEResponse));
  } catch (err) {
    yield put(
      addToast({
        message:
          'Erro inesperado, tente novamente mais tarde. Se o erro persistir, procure o seu vendedor.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadNFXMLFileFailure(nfXmlItem, err as AxiosError));
  }
}
